import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { updateEntity } from 'entities/actions';
import { updateLayerPosition } from '../api';
import { groupLayers, findGroupLayerIds, getBoundingBox } from './layer-geometry';

const rectOf = layer => ({
  xpos: layer?.xpos ?? 0,
  ypos: layer?.ypos ?? 0,
  width: layer?.width ?? 0,
  height: layer?.height ?? 0,
});

const layersToRectMap = layers => {
  const map = {};
  layers.forEach(layer => { map[layer.id] = rectOf(layer); });
  return map;
};

const rectsEqual = (a, b) =>
  !!a && !!b && a.xpos === b.xpos && a.ypos === b.ypos && a.width === b.width && a.height === b.height;

const sessionIsDirty = session =>
  Object.keys(session.edited).some(id => !rectsEqual(session.edited[id], session.baseline[id]));

const makeSession = layers => ({
  baseline: layersToRectMap(layers),
  edited: layersToRectMap(layers),
  historyState: { entries: [], index: -1 },
});

// Client-side editing session for the book's media layers: live position/size
// overrides, undo/redo history, and saving — kept independent of which
// layer/group is currently *selected*.
//
// Design notes:
//  - Nothing is sent to the server until `save()` is called — dragging/
//    resizing only ever touches local React state.
//  - A full drag or resize gesture is ONE history entry (`beginInteraction`
//    snapshots the "before" rects once; `endInteraction`, called on
//    pointer-up, diffs against the live rects and pushes a single entry) —
//    not one entry per pointermove.
//  - Edited/baseline/history are tracked PER PAGE (`sessions`, keyed by page
//    id), not just for whichever page is current. Switching pages only ever
//    resets the active *selection* — a page's pending edits survive
//    navigating away from it and are included the next time save() runs,
//    whichever page is current at that point. This is why `hasUnsavedChanges`
//    / `save()` look across every visited page's session rather than just
//    the current one.
//  - Selection (`selectedLayerIds`) is a separate piece of state from the
//    sessions above — moving, resizing, undoing, redoing or switching pages
//    never touch it (only selectLayer/clearSelection do, plus a successful
//    save per the product's intended "deselect on save" behaviour).
//  - `save()` only PATCHes layers whose rect actually differs from
//    `baseline`, in parallel across every dirty page (the existing `patch()`
//    util already queues/rate-limits requests — see utils/api.js's
//    requestQueue). It uses Promise.allSettled rather than Promise.all so a
//    partial failure still advances `baseline` for whichever layers DID
//    save, leaving only the failed ones as pending changes to retry — a
//    straight Promise.all would make one failure discard a correctly-saved
//    sibling's progress too.
export default function usePageLayerEditor({ firstBookId, currentPage, isLocked }) {
  const dispatch = useDispatch();
  const layers = useMemo(() => currentPage?.layers || [], [currentPage]);
  const pageId = currentPage?.id;

  const [sessions, setSessions] = useState(() => (pageId ? { [pageId]: makeSession(layers) } : {}));
  const [selectedLayerIds, setSelectedLayerIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const interactionRef = useRef(null);

  // Seed a session for the page the member just switched to (lazily, from
  // its own current layers — only the first time it's visited) and clear the
  // active selection, since it's scoped to whichever page is on screen. Any
  // OTHER page's session (its edits/history) is left completely untouched —
  // this is deliberately keyed on pageId alone so a page's own layer data
  // being refetched/re-rendered doesn't blow away in-progress edits.
  useEffect(() => {
    if (!pageId) return;
    setSessions(prev => (prev[pageId] ? prev : { ...prev, [pageId]: makeSession(layers) }));
    setSelectedLayerIds([]);
    setSaveError(null);
  }, [pageId]);

  // Falls back to an ephemeral session on the render where a newly-visited
  // page hasn't been registered in `sessions` yet (the effect above fixes
  // that up immediately after) — avoids reading undefined for one frame.
  // `useMemo` must run unconditionally (Rules of Hooks), so it's computed
  // first and only used via the `||` below when there's no real session yet.
  const fallbackSession = useMemo(() => makeSession(layers), [layers]);
  const session = (pageId && sessions[pageId]) || fallbackSession;
  const { baseline, edited, historyState } = session;

  const updateSession = useCallback(updater => {
    if (!pageId) return;
    setSessions(prev => {
      const current = prev[pageId] || makeSession(layers);
      const next = updater(current);
      if (next === current) return prev;
      return { ...prev, [pageId]: next };
    });
  }, [pageId, layers]);

  const groups = useMemo(() => groupLayers(layers), [layers]);

  const selectLayer = useCallback(layer => {
    if (isLocked) return;
    if (!layer) {
      setSelectedLayerIds([]);
      return;
    }
    setSelectedLayerIds(findGroupLayerIds(layers, layer.id));
  }, [layers, isLocked]);

  const clearSelection = useCallback(() => setSelectedLayerIds([]), []);

  const selectionBox = useMemo(() => {
    const rects = selectedLayerIds.map(id => edited[id]).filter(Boolean);
    return getBoundingBox(rects);
  }, [selectedLayerIds, edited]);

  const getRect = useCallback(
    id => edited[id] || rectOf(layers.find(layer => layer.id === id)),
    [edited, layers]
  );

  // Unlike getRect (always returns a value, used for the selection overlay's
  // own box), this returns null until the layer has actually been moved or
  // resized. The real page layers (NewImageLayer/NewTextLayer) read their
  // position from the Redux entity store via useEntity, not from props — so
  // this is threaded down as an explicit override only for layers that are
  // genuinely dirty, leaving every untouched layer's rendering completely
  // unaffected (no risk of fighting a separately-updated entity, e.g. crop).
  const getOverrideIfDirty = useCallback(
    id => {
      const edit = edited[id];
      const base = baseline[id];
      if (!edit || !base || rectsEqual(edit, base)) return null;
      return edit;
    },
    [edited, baseline]
  );

  // Same idea as getOverrideIfDirty, but for ANY visited page — not just the
  // one currently open for interaction (selection/drag/resize). The render
  // tree keeps every page in the current spread mounted, but only wires up
  // MediaSelectionOverlay/drag for whichever one is `currentPage`; without
  // this, navigating to the other page in the spread (or anywhere else)
  // detaches the live-preview channel from the page you just edited — its
  // unsaved move/resize is still sitting in `sessions`, it just stops being
  // rendered, which looks exactly like the edit vanished until you navigate
  // back and reattach the override.
  const getOverrideForPage = useCallback(
    (pid, id) => {
      const s = sessions[pid];
      if (!s) return null;
      const edit = s.edited[id];
      const base = s.baseline[id];
      if (!edit || !base || rectsEqual(edit, base)) return null;
      return edit;
    },
    [sessions]
  );

  // Call once at pointer-down, before any live updates. Deliberately doesn't
  // touch selection at all — only the gesture's own before/after rects.
  const beginInteraction = useCallback(() => {
    const before = {};
    selectedLayerIds.forEach(id => { before[id] = edited[id]; });
    interactionRef.current = { layerIds: [...selectedLayerIds], before };
  }, [selectedLayerIds, edited]);

  // Call on every pointer-move — cheap, touches only the selected layers'
  // entries, no history write, no selection change.
  const applyLive = useCallback(updates => {
    updateSession(current => ({ ...current, edited: { ...current.edited, ...updates } }));
  }, [updateSession]);

  // Call once at pointer-up — turns the just-finished gesture into a single
  // undoable entry, only if something actually moved/resized. Never touches
  // selectedLayerIds: the element stays selected, handles stay visible.
  const endInteraction = useCallback(() => {
    const start = interactionRef.current;
    interactionRef.current = null;
    if (!start) return;

    updateSession(current => {
      const after = {};
      let didChange = false;
      start.layerIds.forEach(id => {
        after[id] = current.edited[id];
        if (!rectsEqual(current.edited[id], start.before[id])) didChange = true;
      });
      if (!didChange) return current;

      const entries = current.historyState.entries.slice(0, current.historyState.index + 1);
      entries.push({ layerIds: start.layerIds, before: start.before, after });
      return { ...current, historyState: { entries, index: entries.length - 1 } };
    });
  }, [updateSession]);

  // Pointer cancelled (e.g. lost pointer capture) mid-gesture — revert to the
  // pre-interaction rects rather than leaving a half-applied drag in place.
  // Selection is left alone here too.
  const cancelInteraction = useCallback(() => {
    const start = interactionRef.current;
    interactionRef.current = null;
    if (!start) return;
    updateSession(current => ({ ...current, edited: { ...current.edited, ...start.before } }));
  }, [updateSession]);

  const canUndo = historyState.index >= 0;
  const canRedo = historyState.index < historyState.entries.length - 1;

  // Undo/redo only ever replace this page's edited rects — selection,
  // locking, and every other page's session are untouched.
  const undo = useCallback(() => {
    updateSession(current => {
      if (current.historyState.index < 0) return current;
      const entry = current.historyState.entries[current.historyState.index];
      return {
        ...current,
        edited: { ...current.edited, ...entry.before },
        historyState: { ...current.historyState, index: current.historyState.index - 1 },
      };
    });
  }, [updateSession]);

  const redo = useCallback(() => {
    updateSession(current => {
      if (current.historyState.index >= current.historyState.entries.length - 1) return current;
      const nextIndex = current.historyState.index + 1;
      const entry = current.historyState.entries[nextIndex];
      return {
        ...current,
        edited: { ...current.edited, ...entry.after },
        historyState: { ...current.historyState, index: nextIndex },
      };
    });
  }, [updateSession]);

  // Book-wide: true as soon as ANY visited page has a layer that differs
  // from its baseline, not just the page currently on screen — a page's
  // pending edits don't stop counting as "unsaved" just because the member
  // navigated to another page.
  const hasUnsavedChanges = useMemo(
    () => Object.values(sessions).some(sessionIsDirty),
    [sessions]
  );

  // Persists every modified layer on every visited page in one go (not just
  // the current page/selection), so navigating between pages before saving
  // never drops pending work.
  const save = useCallback(async () => {
    const jobs = [];
    Object.entries(sessions).forEach(([sid, s]) => {
      Object.keys(s.edited).forEach(id => {
        if (!rectsEqual(s.edited[id], s.baseline[id])) {
          jobs.push({ pageId: sid, layerId: id, rect: s.edited[id] });
        }
      });
    });
    if (!jobs.length) return true;

    setIsSaving(true);
    setSaveError(null);
    try {
      const results = await Promise.allSettled(
        jobs.map(job => updateLayerPosition(firstBookId, job.pageId, job.layerId, job.rect))
      );
      const succeededJobs = jobs.filter((job, index) => results[index].status === 'fulfilled');
      const anyFailure = succeededJobs.length !== jobs.length;

      if (succeededJobs.length) {
        // NewImageLayer/NewTextLayer render their position/size from the
        // Redux entity store (via useEntity), not from this hook's state —
        // that's a separate "source of truth" the PATCH above never touches.
        // Without updating it too, the new position/size only becomes
        // visible after a full reload re-fetches everything from the
        // server. getOverrideIfDirty also stops covering for a layer the
        // moment it's no longer dirty (right after this save), so the
        // entity has to be correct by then.
        succeededJobs.forEach(job => {
          dispatch(updateEntity({ entityType: 'book-page-layers', id: job.layerId, attrs: job.rect }));
        });

        setSessions(prev => {
          const next = { ...prev };
          succeededJobs.forEach(job => {
            const s = next[job.pageId];
            if (!s) return;
            next[job.pageId] = { ...s, baseline: { ...s.baseline, [job.layerId]: job.rect } };
          });
          // Clear history only for pages that ended up fully clean — a page
          // with a still-failed layer keeps its undo trail.
          Object.keys(next).forEach(sid => {
            const s = next[sid];
            if (!sessionIsDirty(s) && s.historyState.entries.length) {
              next[sid] = { ...s, historyState: { entries: [], index: -1 } };
            }
          });
          return next;
        });
      }

      if (anyFailure) {
        const failure = results.find(result => result.status === 'rejected');
        setSaveError(failure?.reason || new Error('Some changes failed to save'));
        return false;
      }

      // Only on a fully successful save: deselect, per the intended design
      // (moving/resizing/undo/redo/switching pages/selections must never do
      // this — only a completed Save Changes does).
      setSelectedLayerIds([]);
      return true;
    } finally {
      setIsSaving(false);
    }
  }, [sessions, firstBookId, dispatch]);

  // Discards every visited page's pending edits (not just the current
  // page's) — this backs the unsaved-changes guard's "No, don't save", which
  // means walking away from everything pending in the book, not just here.
  const discard = useCallback(() => {
    setSessions(prev => {
      const next = {};
      Object.entries(prev).forEach(([sid, s]) => {
        next[sid] = { ...s, edited: s.baseline, historyState: { entries: [], index: -1 } };
      });
      return next;
    });
    setSelectedLayerIds([]);
    setSaveError(null);
  }, []);

  return useMemo(() => ({
    layers,
    groups,
    getRect,
    selectedLayerIds,
    selectLayer,
    clearSelection,
    selectionBox,
    getOverrideIfDirty,
    getOverrideForPage,
    beginInteraction,
    applyLive,
    endInteraction,
    cancelInteraction,
    canUndo,
    canRedo,
    undo,
    redo,
    hasUnsavedChanges,
    isSaving,
    saveError,
    save,
    discard,
  }), [
    layers,
    groups,
    getRect,
    selectedLayerIds,
    selectLayer,
    clearSelection,
    selectionBox,
    getOverrideIfDirty,
    getOverrideForPage,
    beginInteraction,
    applyLive,
    endInteraction,
    cancelInteraction,
    canUndo,
    canRedo,
    undo,
    redo,
    hasUnsavedChanges,
    isSaving,
    saveError,
    save,
    discard,
  ]);
}
